import React from "react";
import { useTranslation } from "react-i18next";
import { PiRabbit } from "react-icons/pi";
import { cn } from "../utils";
import { typography } from "../tokens";

function RaboRabbitLoader({ compact = false, exiting = false }) {
  const iconSize = compact ? 36 : 56;

  return (
    <div
      className={cn(
        "page-loading-rabbit-stage",
        compact ? "h-16 w-16" : "h-24 w-24",
        exiting && "page-loading-rabbit-stage--settle"
      )}
      aria-hidden="true"
    >
      {!compact && <div className="page-loading-rabbit-halo" />}
      <div className={cn("page-loading-rabbit-shadow", compact && "page-loading-rabbit-shadow--sm")} />
      <div className={cn("page-loading-rabbit-hop", compact && "page-loading-rabbit-hop--sm")}>
        <PiRabbit size={iconSize} className="page-loading-rabbit-icon" strokeWidth={4} />
      </div>
    </div>
  );
}

/**
 * Content-area loading veil — keeps TopBar/Sidebar visible.
 * Used during route transitions and Suspense lazy-load.
 */
export function PageLoadingVeil({ className, message, compact = false, exiting = false }) {
  const { t } = useTranslation();
  const label = message ?? t("app.loading", "Sayfa yükleniyor…");

  if (compact) {
    return (
      <div
        className={cn(
          "page-loading-veil absolute inset-0 z-20 flex items-center justify-center",
          "bg-gray-50/88 backdrop-blur-sm",
          exiting && "page-loading-veil--exit",
          className
        )}
        role="status"
        aria-live="polite"
        aria-busy={!exiting}
        aria-label={label}
      >
        <RaboRabbitLoader compact exiting={exiting} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "page-loading-veil absolute inset-0 z-20 flex items-center justify-center overflow-hidden",
        "bg-gradient-to-br from-slate-50/96 via-white/92 to-blue-50/75 backdrop-blur-md",
        exiting && "page-loading-veil--exit",
        className
      )}
      role="status"
      aria-live="polite"
      aria-busy={!exiting}
      aria-label={label}
    >
      <div className="page-loading-glow" />
      <div className="page-loading-glow page-loading-glow--accent" />

      <div
        className={cn(
          "page-loading-card relative flex flex-col items-center gap-4 text-center",
          "rounded-2xl border border-white/70 bg-white/85 px-12 py-9",
          "shadow-[0_12px_48px_rgba(59,59,59,0.07),0_2px_16px_rgba(37,99,235,0.06)]",
          exiting && "page-loading-card--exit"
        )}
      >
        <RaboRabbitLoader exiting={exiting} />

        <div className="flex flex-col items-center gap-2.5">
          <p className="page-loading-label text-[#3b3b3b]" style={typography.bodyMedium}>
            {label}
          </p>
          <div className="page-loading-bar">
            <div className="page-loading-bar-fill" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Inline spinner for buttons / small panels. */
export function PageSpinner({ className, size = "md" }) {
  const px = size === "sm" ? 16 : size === "lg" ? 28 : 22;
  return (
    <PiRabbit
      size={px}
      strokeWidth={4}
      className={cn("page-loading-rabbit-icon page-loading-rabbit-hop--inline", className)}
      role="status"
      aria-label="Loading"
    />
  );
}
